window.BannerManager = (function () {
    'use strict';

    const BANNER_DELAY = 20 * 1000; // 20 секунд
    const MOBILE_VIEWPORT_QUERY = '(max-width: 600px)';
    const ROOT = document.documentElement;
    let bannerTimer = null;
    let homeBannerObserver = null;
    let bannerResizeObserver = null;
    let bannerMutationObserver = null;
    let commentResizeObserver = null;
    let rootStateObserver = null;
    let bottomLayoutListenersInitialized = false;
    let pendingBottomLayoutFrame = null;
    let bannerTimerCompleted = false; // флаг что таймер отработал

    function getBanner() {
        return document.getElementById('section-banner');
    }

    function getSessionId() {
        return window.session_id?.getSessionId?.();
    }

    function setRootCssVariable(name, value) {
        if (ROOT.style.getPropertyValue(name) === value) return;
        ROOT.style.setProperty(name, value);
    }

    function isMobileViewport() {
        return window.matchMedia(MOBILE_VIEWPORT_QUERY).matches;
    }

    function isKeyboardOpen() {
        return isMobileViewport() && (ROOT.dataset.keyboard === 'open' || ROOT.classList.contains('ios-keyboard-open'));
    }

    function isElementVisible(element) {
        if (!element || element.classList?.contains('hidden')) return false;

        const computedStyles = window.getComputedStyle(element);
        return computedStyles.display !== 'none' && computedStyles.visibility !== 'hidden';
    }

    function syncBannerHeight() {
        const banner = getBanner();

        if (!banner || !isElementVisible(banner) || isKeyboardOpen()) {
            setRootCssVariable('--bottom-banner-h', '0px');
            return;
        }

        setRootCssVariable('--bottom-banner-h', `${Math.ceil(banner.getBoundingClientRect().height)}px`);
    }

    function syncCommentComposerHeight() {
        const commentContainer = document.getElementById('comment-container');

        if (!commentContainer || !isElementVisible(commentContainer)) {
            setRootCssVariable('--comment-composer-h', '0px');
            return;
        }

        setRootCssVariable('--comment-composer-h', `${Math.ceil(commentContainer.getBoundingClientRect().height)}px`);
    }

    function scheduleBottomLayoutSync() {
        if (pendingBottomLayoutFrame !== null) return;

        pendingBottomLayoutFrame = window.requestAnimationFrame(() => {
            pendingBottomLayoutFrame = null;
            syncBannerHeight();
            syncCommentComposerHeight();
        });
    }

    function syncBottomLayout() {
        if (pendingBottomLayoutFrame !== null) {
            window.cancelAnimationFrame(pendingBottomLayoutFrame);
            pendingBottomLayoutFrame = null;
        }

        syncBannerHeight();
        syncCommentComposerHeight();
    }

    function setupBottomLayoutListeners() {
        if (bottomLayoutListenersInitialized) return;

        bottomLayoutListenersInitialized = true;
        window.addEventListener('resize', scheduleBottomLayoutSync);
        window.addEventListener('orientationchange', scheduleBottomLayoutSync);
        window.visualViewport?.addEventListener('resize', scheduleBottomLayoutSync);
        window.visualViewport?.addEventListener('scroll', scheduleBottomLayoutSync);
        document.addEventListener('focusin', scheduleBottomLayoutSync);
        document.addEventListener('focusout', scheduleBottomLayoutSync);
    }

    function setupBottomLayoutObservers() {
        const banner = getBanner();
        const commentContainer = document.getElementById('comment-container');

        bannerResizeObserver?.disconnect();
        bannerResizeObserver = null;
        if (banner && window.ResizeObserver) {
            bannerResizeObserver = new ResizeObserver(() => {
                scheduleBottomLayoutSync();
            });
            bannerResizeObserver.observe(banner);
        }

        commentResizeObserver?.disconnect();
        commentResizeObserver = null;
        if (commentContainer && window.ResizeObserver) {
            commentResizeObserver = new ResizeObserver(() => {
                scheduleBottomLayoutSync();
            });
            commentResizeObserver.observe(commentContainer);
        }

        bannerMutationObserver?.disconnect();
        bannerMutationObserver = null;
        if (banner) {
            bannerMutationObserver = new MutationObserver(() => {
                scheduleBottomLayoutSync();
            });
            bannerMutationObserver.observe(banner, {
                attributes: true,
                attributeFilter: ['class', 'data-state', 'style'],
            });
        }

        if (!rootStateObserver) {
            rootStateObserver = new MutationObserver(() => {
                scheduleBottomLayoutSync();
            });
            rootStateObserver.observe(ROOT, {
                attributes: true,
                attributeFilter: ['class', 'data-keyboard'],
            });
        }

        setupBottomLayoutListeners();
        scheduleBottomLayoutSync();
    }

    function showBanner() {
        const banner = getBanner();
        if (!banner) return;
        banner.classList.remove('hidden');
        syncBottomLayout();
    }

    function hideBanner() {
        const banner = getBanner();
        if (!banner) return;
        banner.classList.add('hidden');
        syncBottomLayout();
    }

    function closeBannerPermanently() {
        hideBanner();
        const sid = getSessionId();
        if (sid) sessionStorage.setItem('banner_closed_' + sid, 'true');
    }

    function canShowBanner() {
        const sid = getSessionId();
        if (!sid) return false;
        return !sessionStorage.getItem('banner_closed_' + sid);
    }

    function cleanOldBannerKeys() {
        const sid = getSessionId();
        if (!sid) return;
        for (const key in sessionStorage) {
            if ((key.startsWith('banner_start_time_') || key.startsWith('banner_closed_')) && !key.endsWith(sid)) {
                sessionStorage.removeItem(key);
            }
        }
    }

    function updateBannerVisibility() {
        const homeBanner = document.getElementById('home-page-banner');
        const banner = getBanner();
        if (!banner) return;

        if (!homeBanner) {
            // если верхнего баннера на странице ленты рекомендаций в неАЗ нет - показываем нижний
            // НО только если таймер уже отработал (для авторизованных) или юзер не авторизован
            if (canShowBanner() && bannerTimerCompleted) showBanner();
            return;
        }

        const rect = homeBanner.getBoundingClientRect();
        const height = rect.height || 1;
        const visibleHeight = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
        const visibleRatio = visibleHeight / height;
        const threshold = 0.25; // 25%

        if (visibleRatio >= threshold) {
            hideBanner();
        } else if (canShowBanner() && bannerTimerCompleted) {
            showBanner();
        }
    }

    function setupHomeBannerObserver() {
        const homeBanner = document.getElementById('home-page-banner');
        if (!homeBanner) {
            homeBannerObserver?.disconnect();
            homeBannerObserver = null;
            return;
        }

        if (homeBannerObserver) homeBannerObserver.disconnect();

        homeBannerObserver = new IntersectionObserver(
            () => {
                updateBannerVisibility();
            },
            { threshold: [0, 0.25, 1], rootMargin: '0px' }
        );

        homeBannerObserver.observe(homeBanner);
    }

    function startBannerTimer() {
        clearTimeout(bannerTimer);

        const banner = getBanner();
        if (!banner) return;

        const isUser = banner.dataset.userAuthenticated === 'true';
        const sid = getSessionId();
        if (!sid) return;

        cleanOldBannerKeys();

        if (!canShowBanner()) return;

        // если пользователь не авторизован — показываем баннер сразу
        if (!isUser) {
            bannerTimerCompleted = true;
            showBanner();
            return;
        }

        // если пользователь авторизован — 20 секунд задержки
        const startKey = 'banner_start_time_' + sid;
        let startTime = Number(sessionStorage.getItem(startKey));

        if (!startTime) {
            startTime = Date.now();
            sessionStorage.setItem(startKey, startTime);
        }

        const elapsed = Date.now() - startTime;
        const remaining = Math.max(BANNER_DELAY - elapsed, 0);

        if (remaining <= 0) {
            bannerTimerCompleted = true;
            showBanner();
        } else {
            bannerTimer = setTimeout(() => {
                bannerTimerCompleted = true;
                if (canShowBanner()) showBanner();
                updateBannerVisibility();
            }, remaining);
        }
    }

    function initBanner() {
        const banner = getBanner();
        if (banner) {
            document.querySelectorAll('.close-banner-btn').forEach(btn => {
                if (!btn.dataset.listenerAdded) {
                    btn.addEventListener('click', closeBannerPermanently);
                    btn.dataset.listenerAdded = 'true';
                }
            });

            startBannerTimer();
            setupHomeBannerObserver();
            updateBannerVisibility(); // на всякий случай сразу проверяем
        } else {
            homeBannerObserver?.disconnect();
            homeBannerObserver = null;
        }

        setupBottomLayoutObservers();
        syncBottomLayout();
    }

    document.addEventListener('DOMContentLoaded', initBanner);
    document.addEventListener('htmx:afterSwap', () => requestAnimationFrame(initBanner));

    return {
        syncBottomLayout,
    };
})();
